import { useEffect, useState, type ReactNode } from "react";
import { formatAccessLogRetentionDaysLabel } from "../accessLogs";
import { lockBodyScroll, unlockBodyScroll } from "../app/bodyScrollLock";
import { logsViewCopy } from "../app/i18n";
import { getIntlLocale, type AppLanguage } from "../app/language";
import { AnimatedModal } from "../app/ui";
import { resolveTimeZone } from "../app/utils";
import { translateUiString } from "../app/viewTranslations";
import type { AccessLog, AuditEvent, WorkspaceSettings } from "../app/types";

type LogsViewProps = {
  language: AppLanguage;
  workspaceSettings: WorkspaceSettings;
  onLoadAccessLogs: (range?: { startDate?: string; endDate?: string; limit?: number }) => Promise<AccessLog[]>;
  onLoadAuditEvents: (range?: { startDate?: string; endDate?: string; limit?: number }) => Promise<AuditEvent[]>;
  onExportAccessLogs: (range: { startDate: string; endDate: string; format?: "csv" | "json" }) => Promise<void>;
  onExportAuditEvents: (range: { startDate: string; endDate: string; format?: "csv" | "json" }) => Promise<void>;
};

const modalCancelButtonClassName = "rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]";
const modalPrimaryButtonClassName = "brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50";

function LogsCard({
  eyebrow,
  title,
  description,
  warning,
  titleActions,
  actions,
  children,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  warning?: ReactNode;
  titleActions?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{eyebrow}</p>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{title}</h2>
            {titleActions ?? null}
          </div>
          {description ? <p className="mt-2 max-w-2xl text-sm text-[#667085]">{description}</p> : null}
          {warning ? <div className="mt-2 max-w-2xl text-sm font-semibold text-[#b42318]">{warning}</div> : null}
        </div>
        {typeof actions === "string" ? <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{actions}</span> : actions ?? null}
      </div>
      {children}
    </section>
  );
}

function CountSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function MetricCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <div className="mt-4 min-h-[2.25rem]">
        {isLoading ? <CountSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="space-y-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{label}</span>
      {children}
    </label>
  );
}

function ModalShell({
  open,
  dialogTitleId,
  backdropAriaLabel,
  eyebrow,
  title,
  description,
  onClose,
  children,
}: {
  open: boolean;
  dialogTitleId: string;
  backdropAriaLabel: string;
  eyebrow: string;
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <AnimatedModal
      open={open}
      onClose={onClose}
      backdropAriaLabel={backdropAriaLabel}
      dialogLabelledBy={dialogTitleId}
      overlayClassName="fixed inset-0 z-[90] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
      dialogClassName="app-modal-dialog relative z-10 flex max-h-[calc(100vh-4rem)] w-full max-w-3xl flex-col rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{eyebrow}</p>
          <h2 id={dialogTitleId} className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
            {title}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[#667085]">{description}</p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
        >
          ×
        </button>
      </div>

      <div className="mt-6 overflow-y-auto pr-1 pb-2">{children}</div>
    </AnimatedModal>
  );
}

function inputClassName() {
  return "w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary-ring)]";
}

function getRelativeDateInput(daysOffset: number) {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().slice(0, 10);
}

function formatTitleCaseSegments(value: string) {
  return value
    .split(/[._]/g)
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function translateLogSegment(segment: string, language: AppLanguage) {
  if (language !== "ja") {
    return segment.charAt(0).toUpperCase() + segment.slice(1);
  }

  const normalized = segment.trim().toLowerCase();
  const segmentCopy: Record<string, string> = {
    access: "アクセス",
    allowed: "許可",
    api: "API",
    article: "記事",
    assignment: "割り当て",
    attachments: "添付ファイル",
    attachment: "添付ファイル",
    audit: "監査",
    calendar: "カレンダー",
    category: "カテゴリ",
    check: "確認",
    collection: "コレクション",
    created: "作成",
    customer: "顧客",
    deleted: "削除",
    denied: "拒否",
    failed: "失敗",
    key: "キー",
    knowledge: "ナレッジ",
    login: "ログイン",
    logout: "ログアウト",
    item: "項目",
    password: "パスワード",
    preset: "プリセット",
    reset: "リセット",
    role: "ロール",
    routes: "ルート",
    session: "セッション",
    set: "設定",
    settings: "設定",
    team: "チーム",
    tenant: "ワークスペース",
    ticket: "チケット",
    type: "種別",
    updated: "更新",
    user: "ユーザー",
    success: "成功",
    workspace: "ワークスペース",
  };

  return segmentCopy[normalized] ?? segment;
}

function formatActionLabel(action: string, language: AppLanguage) {
  return action
    .split(/[._]/g)
    .filter(Boolean)
    .map(segment => translateLogSegment(segment, language))
    .join(" ");
}

function formatTargetTypeLabel(targetType: string, language: AppLanguage) {
  return targetType
    .split(/[_-]/g)
    .filter(Boolean)
    .map(segment => translateLogSegment(segment, language))
    .join(" ");
}

function formatTimestamp(value: string, language: AppLanguage, timezone?: string | null) {
  const timestamp = Date.parse(value);

  if (!Number.isFinite(timestamp)) {
    return value;
  }

  const resolvedTimeZone = resolveTimeZone(timezone);

  return new Date(timestamp).toLocaleString(getIntlLocale(language), {
    ...(resolvedTimeZone ? { timeZone: resolvedTimeZone } : {}),
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function translateLogDetail(value: string, language: AppLanguage) {
  const normalizedValue = value
    .replaceAll("‚Üí", "->")
    .replaceAll("¬∑", " - ")
    .replaceAll("→", "->")
    .replaceAll("·", " - ");

  if (language !== "ja") {
    return normalizedValue;
  }

  const translateAuditFieldLabel = (label: string) => {
    const normalized = label.trim().toLowerCase();
    const fieldLabels: Record<string, string> = {
      "workspace name": "ワークスペース名",
      "portal url": "ポータル URL",
      "support inbox": "サポート受信箱",
      "billing contact": "請求先連絡先",
      timezone: "タイムゾーン",
      currency: "通貨",
      "case email routes": "ケースメールルート",
      "local password reset": "ローカルパスワードリセット",
      "api access": "API アクセス",
      "api key expiry": "API キー有効期限",
      "audit log capture": "監査ログ記録",
      "audit log retention": "監査ログ保持期間",
      "access log capture": "アクセスログ記録",
      "access log retention": "アクセスログ保持期間",
      "log text language": "ログ本文の言語",
    };

    return fieldLabels[normalized] ?? label;
  };

  const translateAuditValue = (segment: string) => {
    const trimmed = segment
      .trim()
      .replaceAll("‚Üí", "->")
      .replaceAll("¬∑", " - ")
      .replaceAll("→", "->")
      .replaceAll("·", " - ");

    if (trimmed === "enabled") {
      return "有効";
    }

    if (trimmed === "disabled") {
      return "無効";
    }

    if (trimmed === "never") {
      return "なし";
    }

    if (/^"en"$/i.test(trimmed)) {
      return "「英語」";
    }

    if (/^"ja"$/i.test(trimmed)) {
      return "「日本語」";
    }

    const dayMatch = trimmed.match(/^(\d+) days$/i);

    if (dayMatch) {
      return `${dayMatch[1]} 日`;
    }

    return trimmed;
  };

  const translatedExact = translateUiString(normalizedValue, language);

  if (translatedExact !== normalizedValue) {
    return translatedExact;
  }

  const patternTranslations: Array<[RegExp, (...matches: string[]) => string]> = [
    [/^Automatically created after receiving an email from (.+)\.$/, (sender) => `${sender} からのメールを受信したため自動作成されました。`],
    [/^Login succeeded for (.+) on (.+)\.$/, (email, host) => `${host} で ${email} のログインに成功しました。`],
    [/^Login failed for (.+) on (.+)\.$/, (email, host) => `${host} で ${email} のログインに失敗しました。`],
    [/^(.+) signed out of (.+)\.$/, (actor, host) => `${actor} は ${host} からログアウトしました。`],
    [/^A logout attempt failed on (.+)\.$/, (host) => `${host} でログアウトの試行に失敗しました。`],
    [/^Session check allowed (.+) to access (.+)\.$/, (actor, host) => `セッション確認により ${actor} の ${host} へのアクセスを許可しました。`],
    [/^Session check denied access to (.+)\.$/, (host) => `${host} へのアクセスはセッション確認で拒否されました。`],
    [/^Denied API access to (.+) for an unauthenticated request on (.+)\.$/, (pathname, host) => `${host} 上の未認証リクエストに対する ${pathname} への API アクセスを拒否しました。`],
    [/^Updated workspace settings: (.+)\.$/, (changes) => {
      const translatedChanges = changes
        .split("; ")
        .map(change => {
          const match = change.match(/^(.+?) (.+) (?:→|->) (.+)$/);

          if (!match) {
            return change;
          }

          const fieldLabel = match[1] ?? "";
          const previousValue = match[2] ?? "";
          const nextValue = match[3] ?? "";
          return `${translateAuditFieldLabel(fieldLabel)} ${translateAuditValue(previousValue)} -> ${translateAuditValue(nextValue)}`;
        })
        .join("、");

      return `ワークスペース設定を更新しました: ${translatedChanges}。`;
    }],
    [/^Created calendar assignment (.+) for (.+)\.$/, (title, email) => `${email} 向けのカレンダー割り当て「${title}」を作成しました。`],
    [/^Updated calendar assignment (.+) for (.+)\.$/, (title, email) => `${email} 向けのカレンダー割り当て「${title}」を更新しました。`],
    [/^Deleted calendar assignment (.+) for (.+)\.$/, (title, email) => `${email} 向けのカレンダー割り当て「${title}」を削除しました。`],
    [/^Updated the local password for (.+)\.$/, (email) => `${email} のローカルパスワードを更新しました。`],
    [/^Created API key (.+)\.$/, (label) => `API キー ${label} を作成しました。`],
    [/^Revoked API key (.+)\.$/, (label) => `API キー ${label} を無効化しました。`],
    [/^Created workspace user (.+)\.$/, (name) => `ワークスペースユーザー ${name} を作成しました。`],
    [/^(Set|Reset) the local password for (.+)\.$/, (mode, email) => `${email} のローカルパスワードを${mode === "Set" ? "設定" : "リセット"}しました。`],
    [/^Created team (.+)\.$/, (name) => `チーム ${name} を作成しました。`],
    [/^Deleted team (.+)\.$/, (name) => `チーム ${name} を削除しました。`],
    [/^Created role preset (.+)\.$/, (name) => `ロールプリセット ${name} を作成しました。`],
    [/^Deleted role preset (.+)\.$/, (name) => `ロールプリセット ${name} を削除しました。`],
    [/^Created customer (.+)\.$/, (name) => `顧客 ${name} を作成しました。`],
    [/^Deleted customer (.+)\.$/, (name) => `顧客 ${name} を削除しました。`],
    [/^Created ticket (.+)\.$/, (id) => `チケット ${id} を作成しました。`],
    [/^Added (.+) to (.+)\.$/, (count, id) => `${id} に ${count} を追加しました。`],
    [/^Removed attachment (.+) from (.+)\.$/, (name, id) => `${id} から添付ファイル ${name} を削除しました。`],
    [/^Deleted ticket (.+)\.$/, (id) => `チケット ${id} を削除しました。`],
    [/^Created knowledge collection (.+)\.$/, (name) => `ナレッジコレクション ${name} を作成しました。`],
    [/^Deleted knowledge collection (.+)\.$/, (name) => `ナレッジコレクション ${name} を削除しました。`],
    [/^Created knowledge article (.+)\.$/, (name) => `ナレッジ記事 ${name} を作成しました。`],
    [/^Deleted knowledge article (.+)\.$/, (name) => `ナレッジ記事 ${name} を削除しました。`],
    [/^Created item (.+)\.$/, (name) => `項目 ${name} を作成しました。`],
    [/^Deleted item (.+)\.$/, (name) => `項目 ${name} を削除しました。`],
    [/^Created ticket category (.+)\.$/, (name) => `チケットカテゴリ ${name} を作成しました。`],
    [/^Deleted ticket category (.+)\.$/, (name) => `チケットカテゴリ ${name} を削除しました。`],
  ];

  for (const [pattern, formatter] of patternTranslations) {
    const match = normalizedValue.match(pattern);

    if (match) {
      return formatter(...match.slice(1));
    }
  }

  if (normalizedValue === "Saved workspace settings without changing any values.") {
    return "ワークスペース設定に変更はありませんでした。";
  }

  return normalizedValue;
}

export function LogsView({
  language,
  workspaceSettings,
  onLoadAccessLogs,
  onLoadAuditEvents,
  onExportAccessLogs,
  onExportAuditEvents,
}: LogsViewProps) {
  const copy = logsViewCopy[language];
  const logTextLanguage = workspaceSettings.logTextLanguage ?? "en";
  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([]);
  const [accessLogsBusy, setAccessLogsBusy] = useState(false);
  const [accessLogsError, setAccessLogsError] = useState<string | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditEventsBusy, setAuditEventsBusy] = useState(false);
  const [auditEventsError, setAuditEventsError] = useState<string | null>(null);
  const [isAccessExportModalOpen, setIsAccessExportModalOpen] = useState(false);
  const [isAuditExportModalOpen, setIsAuditExportModalOpen] = useState(false);
  const [accessExportStartDate, setAccessExportStartDate] = useState(() => getRelativeDateInput(-30));
  const [accessExportEndDate, setAccessExportEndDate] = useState(() => getRelativeDateInput(0));
  const [auditExportStartDate, setAuditExportStartDate] = useState(() => getRelativeDateInput(-30));
  const [auditExportEndDate, setAuditExportEndDate] = useState(() => getRelativeDateInput(0));
  const [accessExportBusy, setAccessExportBusy] = useState(false);
  const [auditExportBusy, setAuditExportBusy] = useState(false);
  const metricsLoading = auditEventsBusy || accessLogsBusy;
  const [accessExportError, setAccessExportError] = useState<string | null>(null);
  const [auditExportError, setAuditExportError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setAccessLogsBusy(true);
      setAccessLogsError(null);
      setAuditEventsBusy(true);
      setAuditEventsError(null);

      try {
        const [nextAccessLogs, nextAuditEvents] = await Promise.all([
          onLoadAccessLogs({ limit: 10 }),
          onLoadAuditEvents({ limit: 10 }),
        ]);

        if (!cancelled) {
          setAccessLogs(nextAccessLogs);
          setAuditEvents(nextAuditEvents);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : copy.errors.loadLogs;
          setAccessLogsError(message);
          setAuditEventsError(message);
        }
      } finally {
        if (!cancelled) {
          setAccessLogsBusy(false);
          setAuditEventsBusy(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [copy.errors.loadLogs, onLoadAccessLogs, onLoadAuditEvents]);

  useEffect(() => {
    if ((!isAccessExportModalOpen && !isAuditExportModalOpen) || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      if (isAuditExportModalOpen) {
        setIsAuditExportModalOpen(false);
        setAuditExportError(null);
        return;
      }

      if (isAccessExportModalOpen) {
        setIsAccessExportModalOpen(false);
        setAccessExportError(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAccessExportModalOpen, isAuditExportModalOpen]);

  const handleRefreshAccessLogs = async () => {
    setAccessLogsBusy(true);
    setAccessLogsError(null);

    try {
      setAccessLogs(await onLoadAccessLogs({ limit: 10 }));
    } catch (error) {
      setAccessLogsError(error instanceof Error ? error.message : copy.errors.loadAccess);
    } finally {
      setAccessLogsBusy(false);
    }
  };

  const handleRefreshAuditEvents = async () => {
    setAuditEventsBusy(true);
    setAuditEventsError(null);

    try {
      setAuditEvents(await onLoadAuditEvents({ limit: 10 }));
    } catch (error) {
      setAuditEventsError(error instanceof Error ? error.message : copy.errors.loadAudit);
    } finally {
      setAuditEventsBusy(false);
    }
  };

  const handleExportAccessLogsClick = async () => {
    if (!accessExportStartDate || !accessExportEndDate) {
      setAccessExportError(copy.errors.chooseRange);
      return;
    }

    if (accessExportStartDate > accessExportEndDate) {
      setAccessExportError(copy.errors.invalidRange);
      return;
    }

    setAccessExportBusy(true);
    setAccessExportError(null);

    try {
      await onExportAccessLogs({ startDate: accessExportStartDate, endDate: accessExportEndDate, format: "csv" });
      setIsAccessExportModalOpen(false);
    } catch (error) {
      setAccessExportError(error instanceof Error ? error.message : copy.errors.exportAccess);
    } finally {
      setAccessExportBusy(false);
    }
  };

  const handleExportAuditLogsClick = async () => {
    if (!auditExportStartDate || !auditExportEndDate) {
      setAuditExportError(copy.errors.chooseRange);
      return;
    }

    if (auditExportStartDate > auditExportEndDate) {
      setAuditExportError(copy.errors.invalidRange);
      return;
    }

    setAuditExportBusy(true);
    setAuditExportError(null);

    try {
      await onExportAuditEvents({ startDate: auditExportStartDate, endDate: auditExportEndDate, format: "csv" });
      setIsAuditExportModalOpen(false);
    } catch (error) {
      setAuditExportError(error instanceof Error ? error.message : copy.errors.exportAudit);
    } finally {
      setAuditExportBusy(false);
    }
  };

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.page.title}</h1>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">{copy.page.description}</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label={copy.metrics.auditCapture} value={workspaceSettings.auditLogsEnabled ? copy.states.on : copy.states.off} detail={workspaceSettings.auditLogsEnabled ? copy.metrics.auditEnabled : copy.metrics.auditDisabled} isLoading={metricsLoading} />
        <MetricCard label={copy.metrics.accessCapture} value={workspaceSettings.accessLogsEnabled ? copy.states.on : copy.states.off} detail={workspaceSettings.accessLogsEnabled ? copy.metrics.accessEnabled : copy.metrics.accessDisabled} isLoading={metricsLoading} />
        <MetricCard
          label={copy.metrics.recentAuditEvents}
          value={String(auditEvents.length).padStart(2, "0")}
          detail={copy.metrics.retentionPolicy(workspaceSettings.auditLogRetentionDays ? formatAccessLogRetentionDaysLabel(workspaceSettings.auditLogRetentionDays, language) : copy.metrics.neverAutoDelete)}
          isLoading={metricsLoading}
        />
        <MetricCard
          label={copy.metrics.recentAccessEvents}
          value={String(accessLogs.length).padStart(2, "0")}
          detail={copy.metrics.retentionPolicy(workspaceSettings.accessLogRetentionDays ? formatAccessLogRetentionDaysLabel(workspaceSettings.accessLogRetentionDays, language) : copy.metrics.neverAutoDelete)}
          isLoading={metricsLoading}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <LogsCard
          eyebrow={copy.audit.eyebrow}
          title={copy.audit.title}
          description={copy.audit.description}
          warning={!workspaceSettings.auditLogsEnabled ? copy.audit.disabledWarning : undefined}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void handleRefreshAuditEvents()}
                disabled={auditEventsBusy}
                className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {auditEventsBusy ? copy.states.loading : copy.states.refresh}
              </button>
              <button
                type="button"
                onClick={() => setIsAuditExportModalOpen(true)}
                className="brand-primary-button rounded-xl px-4 py-2 text-sm font-bold transition-all"
              >
                {copy.states.export}
              </button>
            </div>
          }
        >
          <div className="rounded-[24px] border border-[#eaecf0] bg-[#fcfcfd] p-5">
            {auditEventsError ? <p className="text-sm font-medium text-[#b42318]">{auditEventsError}</p> : null}
            {!auditEventsError && auditEvents.length === 0 && !auditEventsBusy ? (
              <p className="text-sm text-[#667085]">{copy.audit.empty}</p>
            ) : null}

            <div className="space-y-3">
              {auditEvents.map(event => (
                <article key={event.id} className="rounded-[20px] border border-[#eaecf0] bg-white px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[var(--brand-primary-soft)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--brand-primary)]">
                          {formatActionLabel(event.action, logTextLanguage)}
                        </span>
                        <span className="rounded-full bg-[#f3f4f6] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#475467]">
                          {formatTargetTypeLabel(event.targetType, logTextLanguage)}
                        </span>
                      </div>
                      <p className="mt-3 text-sm font-semibold text-[#101828]">{translateLogDetail(event.detail, logTextLanguage)}</p>
                      <p className="mt-1 text-sm text-[#667085]">
                        {event.actor}
                        {event.actorEmail ? ` · ${event.actorEmail}` : ""}
                        {event.targetId ? ` · ${event.targetId}` : ""}
                      </p>
                    </div>
                    <p className="text-xs font-medium text-[#98a2b3]">{formatTimestamp(event.createdAt, language, workspaceSettings.timezone)}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </LogsCard>

        <LogsCard
          eyebrow={copy.access.eyebrow}
          title={copy.access.title}
          description={copy.access.description}
          warning={!workspaceSettings.accessLogsEnabled ? copy.access.disabledWarning : undefined}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void handleRefreshAccessLogs()}
                disabled={accessLogsBusy}
                className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {accessLogsBusy ? copy.states.loading : copy.states.refresh}
              </button>
              <button
                type="button"
                onClick={() => setIsAccessExportModalOpen(true)}
                className="brand-primary-button rounded-xl px-4 py-2 text-sm font-bold transition-all"
              >
                {copy.states.export}
              </button>
            </div>
          }
        >
          <div className="rounded-[24px] border border-[#eaecf0] bg-[#fcfcfd] p-5">
            {accessLogsError ? <p className="text-sm font-medium text-[#b42318]">{accessLogsError}</p> : null}
            {!accessLogsError && accessLogs.length === 0 && !accessLogsBusy ? (
              <p className="text-sm text-[#667085]">{copy.access.empty}</p>
            ) : null}

            <div className="space-y-3">
              {accessLogs.map(entry => (
                <article key={entry.id} className="rounded-[20px] border border-[#eaecf0] bg-white px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#ecfdf3] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#027a48]">
                          {formatActionLabel(entry.action, logTextLanguage)}
                        </span>
                        <span className="rounded-full bg-[#f3f4f6] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#475467]">
                          {formatTargetTypeLabel(entry.targetType, logTextLanguage)}
                        </span>
                      </div>
                      <p className="mt-3 text-sm font-semibold text-[#101828]">{translateLogDetail(entry.detail, logTextLanguage)}</p>
                      <p className="mt-1 text-sm text-[#667085]">
                        {entry.actor}
                        {entry.actorEmail ? ` · ${entry.actorEmail}` : ""}
                        {entry.ipAddress ? ` · ${entry.ipAddress}` : ""}
                      </p>
                      {entry.userAgent ? <p className="mt-1 text-xs text-[#98a2b3]">{entry.userAgent}</p> : null}
                    </div>
                    <p className="text-xs font-medium text-[#98a2b3]">{formatTimestamp(entry.occurredAt, language, workspaceSettings.timezone)}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </LogsCard>
      </div>

      <ModalShell
        open={isAuditExportModalOpen}
        dialogTitleId="workspace-audit-log-export-dialog-title"
        backdropAriaLabel={copy.modals.closeDialog}
        eyebrow={copy.modals.audit.eyebrow}
        title={copy.modals.audit.title}
        description={copy.modals.audit.description}
        onClose={() => {
          setIsAuditExportModalOpen(false);
          setAuditExportError(null);
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label={copy.modals.startDate}>
            <input type="date" className={inputClassName()} value={auditExportStartDate} onChange={event => setAuditExportStartDate(event.target.value)} />
          </FormField>
          <FormField label={copy.modals.endDate}>
            <input type="date" className={inputClassName()} value={auditExportEndDate} onChange={event => setAuditExportEndDate(event.target.value)} />
          </FormField>
        </div>

        {auditExportError ? <p className="mt-4 text-sm font-medium text-[#b42318]">{auditExportError}</p> : null}

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button className={modalCancelButtonClassName} onClick={() => {
            setIsAuditExportModalOpen(false);
            setAuditExportError(null);
          }}>
            {copy.states.cancel}
          </button>
          <button className={modalPrimaryButtonClassName} disabled={auditExportBusy} onClick={() => void handleExportAuditLogsClick()}>
            {auditExportBusy ? copy.states.exporting : copy.states.exportLogs}
          </button>
        </div>
      </ModalShell>

      <ModalShell
        open={isAccessExportModalOpen}
        dialogTitleId="workspace-access-log-export-dialog-title"
        backdropAriaLabel={copy.modals.closeDialog}
        eyebrow={copy.modals.access.eyebrow}
        title={copy.modals.access.title}
        description={copy.modals.access.description}
        onClose={() => {
          setIsAccessExportModalOpen(false);
          setAccessExportError(null);
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label={copy.modals.startDate}>
            <input type="date" className={inputClassName()} value={accessExportStartDate} onChange={event => setAccessExportStartDate(event.target.value)} />
          </FormField>
          <FormField label={copy.modals.endDate}>
            <input type="date" className={inputClassName()} value={accessExportEndDate} onChange={event => setAccessExportEndDate(event.target.value)} />
          </FormField>
        </div>

        {accessExportError ? <p className="mt-4 text-sm font-medium text-[#b42318]">{accessExportError}</p> : null}

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button className={modalCancelButtonClassName} onClick={() => {
            setIsAccessExportModalOpen(false);
            setAccessExportError(null);
          }}>
            {copy.states.cancel}
          </button>
          <button className={modalPrimaryButtonClassName} disabled={accessExportBusy} onClick={() => void handleExportAccessLogsClick()}>
            {accessExportBusy ? copy.states.exporting : copy.states.exportLogs}
          </button>
        </div>
      </ModalShell>
    </section>
  );
}
